import React, { Dispatch, SetStateAction } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { format } from 'date-fns';

import { UseOutletContextPurchase } from '../Purchase';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { legendInvalid, legendValid } from '../../../../utils/legend.data.util';
import { evaluateLegendRequired } from '../../../../scripts/validate.legend.script';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { contactTypeValue } from '../../../../libraries/contact.type.library';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { formatContact } from '../../../../scripts/format.value.script';

import AppLegend from '../../../../components/element/Legend';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';
import AppInputPaymentMethod from '../../../../components/element/InputPaymentMethod';
import AppInputContact from '../../../../components/element/InputContact';
import AppInputForeignCurrency, { ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';

export interface AppPurchaseFormUpdateProps {
  purchase: string,
  record: any | ProviderOrder | ProviderInvoice | Expense,
  provider: {value: string, valid: boolean, object: any},
  concerning: {value: string, valid: boolean},
  date: {value: string, valid: boolean},
  currency: {value: string, valid: boolean, object: any},
  treasuryAccount?: {value: string, valid: boolean, object: any},
  paymentMethod?: {value: string, valid: boolean},
  foreignCurrency?: ForeignCurrencyState,
  setProvider: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setConcerning: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setTreasuryAccount?: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setPaymentMethod?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setForeignCurrency?: Dispatch<SetStateAction<ForeignCurrencyState>>
};

const AppPurchaseFormUpdate: React.FunctionComponent<AppPurchaseFormUpdateProps> = ({purchase, record, provider, concerning, date, currency, treasuryAccount, paymentMethod, foreignCurrency, setProvider, setConcerning, setDate, setCurrency, setPaymentMethod, setTreasuryAccount, setForeignCurrency}) => {
  const {appServiceForUser, companyForUser, treasuryAccountsForCompany, contactsForCompany, loadContactsForCompany} = UseOutletContextPurchase()
  const {lang} = StateLanguage()

  const handleChangeConcerning = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setConcerning({...concerning, value: event.target.value})
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({value: event.target.value, valid: true})
      legendValid('legend-date-required')
    } else {
      setDate({value: event.target.value, valid: false})
      legendInvalid('legend-date-required')
    }
  }

  const validateConcerning = () => {
    evaluateLegendRequired(expressions.text, concerning, setConcerning, 'legend-concerning-valid', 'legend-concerning-required')
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  return (
    <>
      { appServiceForUser && companyForUser && treasuryAccountsForCompany && contactsForCompany &&
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              <h2>
                {purchase === "order" && record.provider_order_number}
                {purchase === "invoice" && record.provider_invoice_number}
                {purchase === "expense" && record.expense_number}
                { record.status === "draft" &&
                  <span className="text-lowercase ms-2">({lang.labels.draft})</span>
                }
              </h2>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="d-flex flex-wrap mb-5">
              <div className="flex-equal me-5">
                <table className="table align-middle gy-1 m-0">
                  <tbody className="fw-bold">
                    <tr>
                      <td className="w-200px text-muted">{lang.labels.company}:</td>
                      <td className="min-w-200px">{record.company.name}</td>
                    </tr>
                    { record.status !== "draft" &&
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.provider}:</td>
                        <td className="min-w-200px">
                          { record.provider
                            ?
                            <div className="d-flex align-items-center">
                              {formatContact(record.provider)}
                              <span className="badge badge-sm badge-light text-uppercase fs-9 ms-3">{contactTypeValue(lang, record.provider.type)}</span>
                            </div>
                            :
                            <span>—</span>
                          }
                        </td>
                      </tr>
                    }
                    <tr>
                      <td className="w-200px text-muted">{lang.labels.createdAt}:</td>
                      <td className="min-w-200px">{datetimeFormat12h(lang.code, record.created_at)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex-equal">
                <table className="table align-middle gy-1 m-0">
                  <tbody className="fw-bold">
                    { purchase !== "expense" && record.status !== "draft" &&
                      <tr>
                        <td className="w-200px text-muted">{lang.labels[`${purchase !== "expense" ? "provider" : ""}${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}Reference` as keyof typeof lang.labels]}:</td>
                        <td className="min-w-200px">{(record.reference ?? '').length > 0 ? record.reference : lang.labels.none}</td>
                      </tr>
                    }
                    { purchase === "invoice" && record.status !== "draft" &&
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.providerOrderNumber}:</td>
                        <td className="min-w-200px">{record.id_provider_order ? (record.provider_order?.provider_order_number ?? record.id_provider_order) : lang.labels.none}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
            <div className="fw-bold fs-6 mb-5">{lang.labels[`pleaseModifyAddOrDeleteFieldsFrom${purchase !== "expense" ? "Provider" : ""}${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}` as keyof typeof lang.labels]}</div>
            { record.status === "draft" &&
              <div className="row mb-3">
                <label className={`form-label ${purchase !== "expense" ? "required" : ""}`}>{lang.labels.provider}</label>
                <div className="fv-row">
                  <AppInputContact appServiceForUser={appServiceForUser} companyForUser={companyForUser} contact={provider} setContact={setProvider} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
                </div>
              </div>
            }
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.concerning}</label>
              <div className="fv-row">
                <TextareaAutosize className="form-control" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
                <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.creationDate}</label>
              <div className="fv-row">
                <input className="form-control" type="date" name="date" max={currentDate()} value={date.value.substring(0, 10)} onChange={handleChangeDate} />
                <AppLegend attribute={{check: date.valid, name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.currency}</label>
              <div className="fv-row">
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
              </div>
            </div>
            { currency.value && companyForUser && currency.value !== companyForUser.currency?.code && foreignCurrency && setForeignCurrency &&
              <div className="row mb-3">
                <label className="form-label">{lang.labels.exchangeRate}</label>
                <div className="fv-row">
                  <AppInputForeignCurrency companyId={companyForUser.id} companyCurrencyCode={companyForUser.currency?.code} selectedCurrency={currency.object} foreignCurrency={foreignCurrency} setForeignCurrency={setForeignCurrency}></AppInputForeignCurrency>
                </div>
              </div>
            }
            { treasuryAccount && setTreasuryAccount &&
              <div className={`row ${paymentMethod && setPaymentMethod && "mb-3"}`}>
                <label className="form-label required">{lang.labels.treasuryAccount}</label>
                <div className="fv-row">
                  <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount}></AppInputTreasuryAccount>
                </div>
              </div>
            }
            { paymentMethod && setPaymentMethod &&
              <div className="row">
                <label className="form-label required">{lang.labels.paymentMethod}</label>
                <div className="fv-row">
                  <AppInputPaymentMethod solid={true} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}></AppInputPaymentMethod>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </>
  )
};

export default AppPurchaseFormUpdate;
